import React, {useEffect, useState, useRef} from 'react'
import {useCustomQuery} from '@salesforce/commerce-sdk-react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useUsid} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getDeliveryOptionsFallback} from '../utils/fallback'

const GEOLOCATION_KEY_PREFIX = 'geolocation_'

const useDeliveryOptions = ({accessToken = null, cache = false}) => {
    const [isLoading, setIsLoading] = useState(true)
    const [deliveryOptionResponse, setDeliveryOptionResponse] = useState(null)
    const {usid} = useUsid()
    const {site} = useMultiSite()
    const cacheKey = `deliveryOptions_${site.id}`
    const geolocationKey = `${GEOLOCATION_KEY_PREFIX}${site.id}`
    const hasFetched = useRef(false);

    const {refetch} = useCustomQuery(
        {
            options: {
                method: 'POST',
                parameters: {
                    siteId: site.id,
                    c_usid: usid
                },
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/delivery-options',
                    apiName: 'pdp'
                },
                body: JSON.stringify({
                    accessToken: accessToken
                })
            },
            rawResponse: false
        },
        {
            enabled: false,
            onSuccess: (response) => {
                setIsLoading(false)
                if (!response?.success || !response?.deliveryOptions) {
                    setDeliveryOptionResponse(getDeliveryOptionsFallback(site.id))
                    return
                }
                setDeliveryOptionResponse(response)

                const { geolocation, ...deliveryOptions } = response

                try {
                    if (cache && !response.isFallback) {
                        sessionStorage.setItem(cacheKey, JSON.stringify(deliveryOptions))
                        if (geolocation?.PostalCode && geolocation?.CountryCode) {
                            sessionStorage.setItem(
                                geolocationKey,
                                JSON.stringify({
                                    postalCode: String(geolocation.PostalCode),
                                    countryCode: geolocation.CountryCode
                                })
                            )
                        }
                    } else {
                        sessionStorage.removeItem(cacheKey)
                    }
                } catch (error) {
                    // Storage is optional; the response can still be displayed.
                }
            },
            onError: (error) => {
                setIsLoading(false)
                setDeliveryOptionResponse(getDeliveryOptionsFallback(site.id))
                logger.error('Error getting delivery options:', {
                    namespace: 'Delivery options',
                    additionalProperties: {error: error.message, stack: error.stack}
                })
            }
        }
    )

    useEffect(() => {
        if(accessToken !== null && !hasFetched.current) {
            let cachedData
            try {
                cachedData = sessionStorage.getItem(cacheKey)
            } catch (error) {
                // Fetch normally when browser storage is unavailable.
            }

            if (cachedData) {
                try {
                    const response = JSON.parse(cachedData)
                    if (!response?.success || !response?.deliveryOptions) {
                        throw new Error('Invalid cached delivery options')
                    }
                    setDeliveryOptionResponse(response)
                } catch (error) {
                    logger.error('Error parsing delivery options:', {
                        namespace: 'Delivery options',
                        additionalProperties: {error: error.message, stack: error.stack}
                    })
                    setDeliveryOptionResponse(getDeliveryOptionsFallback(site.id))
                }

                setIsLoading(false)
            } else {
                refetch()
            }

            hasFetched.current = true;
        }
    }, [accessToken])


    return {
        isLoading,
        deliveryOptionResponse
    }
}

export {useDeliveryOptions, GEOLOCATION_KEY_PREFIX}
